import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { isTokenExpired } from '../utils/jwt';

interface AdminAuthState {
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requiresPasswordChange: boolean;
  session: string | null; // For password change challenge
  pendingEmail: string | null; // Email for password change challenge
  signIn: (email: string, password: string) => Promise<void>;
  changePassword: (newPassword: string) => Promise<void>;
  signOut: () => void;
  refreshAccessToken: () => Promise<void>;
  clearError: () => void;
  initializeTokenRefresh: () => void;
  getAccessToken: () => string | null;
  getIdToken: () => string | null;
}

// API configuration
import { getApiUrl, getApiKey } from '../utils/apiConfig';

// Get API configuration (throws if not configured)
const API_URL = getApiUrl();
const API_KEY = getApiKey();

// Helper to get headers with optional API key
const getHeaders = (): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (API_KEY) {
    headers['X-Api-Key'] = API_KEY;
  }
  return headers;
};

export const useAdminAuthStore = create<AdminAuthState>()(
  persist(
    (set, get) => {
      const initialState = {
        accessToken: null,
        refreshToken: null,
        idToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        requiresPasswordChange: false,
        session: null,
        pendingEmail: null,
      };

      return {
        ...initialState,

        signIn: async (email: string, password: string) => {
          set({ isLoading: true, error: null });
          try {
            const response = await fetch(`${API_URL}/auth/admin/signin`, {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.message || 'Sign in failed');
            }

            const data = await response.json();

            // Check if password change is required
            if (data.requiresPasswordChange) {
              set({
                isLoading: false,
                requiresPasswordChange: true,
                session: data.session || null,
                pendingEmail: email,
                error: null,
              });
              return; // Don't throw error, let the UI handle password change
            }

            if (!data.accessToken || !data.refreshToken || !data.idToken) {
              throw new Error('Missing authentication tokens');
            }

            set({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              idToken: data.idToken,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              requiresPasswordChange: false,
              session: null,
              pendingEmail: null,
            });
          } catch (err: any) {
            let errorMessage = 'Failed to sign in';
            if (err.message?.includes('Invalid email or password')) {
              errorMessage = 'Invalid email or password';
            } else if (err.message?.includes('User not found')) {
              errorMessage = 'User not found';
            } else if (err.message?.includes('not confirmed')) {
              errorMessage = 'User account is not confirmed';
            } else if (err.message) {
              errorMessage = err.message;
            }

            set({
              isLoading: false,
              error: errorMessage,
              isAuthenticated: false,
              accessToken: null,
              refreshToken: null,
              idToken: null,
              requiresPasswordChange: false,
              session: null,
              pendingEmail: null,
            });
            throw new Error(errorMessage);
          }
        },

        changePassword: async (newPassword: string) => {
          const { session, pendingEmail } = get();
          if (!session || !pendingEmail) {
            throw new Error('No active session for password change');
          }

          set({ isLoading: true, error: null });
          try {
            const response = await fetch(`${API_URL}/auth/admin/signin`, {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({
                email: pendingEmail,
                newPassword,
                session,
              }),
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.message || 'Password change failed');
            }

            const data = await response.json();

            if (!data.accessToken || !data.refreshToken || !data.idToken) {
              throw new Error('Missing authentication tokens');
            }

            set({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              idToken: data.idToken,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              requiresPasswordChange: false,
              session: null,
              pendingEmail: null,
            });
          } catch (err: any) {
            let errorMessage = 'Failed to change password';
            if (err.message?.includes('Password does not meet requirements')) {
              errorMessage = 'Password does not meet requirements (min 8 characters, uppercase, lowercase, number)';
            } else if (err.message?.includes('Invalid session')) {
              errorMessage = 'Invalid session. Please try signing in again.';
            } else if (err.message) {
              errorMessage = err.message;
            }

            set({
              isLoading: false,
              error: errorMessage,
            });
            throw new Error(errorMessage);
          }
        },

        signOut: () => {
          set({
            accessToken: null,
            refreshToken: null,
            idToken: null,
            isAuthenticated: false,
            error: null,
            requiresPasswordChange: false,
            session: null,
            pendingEmail: null,
          });
        },

        refreshAccessToken: async () => {
          const { refreshToken } = get();
          if (!refreshToken) {
            set({ isAuthenticated: false, accessToken: null, idToken: null });
            return;
          }

          try {
            const response = await fetch(`${API_URL}/auth/admin/refresh`, {
              method: 'POST',
              headers: getHeaders(),
              body: JSON.stringify({ refreshToken }),
            });

            if (!response.ok) {
              throw new Error('Token refresh failed');
            }

            const data = await response.json();

            if (!data.accessToken || !data.idToken) {
              throw new Error('Missing tokens from refresh');
            }

            set({
              accessToken: data.accessToken,
              idToken: data.idToken,
              isAuthenticated: true,
            });
          } catch (err) {
            set({ isAuthenticated: false, accessToken: null, refreshToken: null, idToken: null });
            throw err;
          }
        },

        clearError: () => {
          set({ error: null });
        },

        getAccessToken: () => {
          return get().accessToken;
        },

        getIdToken: () => {
          return get().idToken;
        },

        initializeTokenRefresh: () => {
          const checkAndRefreshTokens = async () => {
            const state = get();
            if (!state.isAuthenticated || !state.refreshToken) {
              return;
            }

            const idTokenExpired = isTokenExpired(state.idToken, 600);
            const accessTokenExpired = isTokenExpired(state.accessToken, 600);

            if (idTokenExpired || accessTokenExpired) {
              try {
                await state.refreshAccessToken();
              } catch (error) {
                console.error('Periodic token refresh failed:', error);
              }
            }
          };

          checkAndRefreshTokens();
          const intervalId = setInterval(checkAndRefreshTokens, 10 * 60 * 1000);
          return () => clearInterval(intervalId);
        },
      };
    },
    {
      name: 'admin-auth-storage',
      partialize: (state: AdminAuthState) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        idToken: state.idToken,
        // Don't persist password change state
      }),
      onRehydrateStorage: () => async (state) => {
        if (state) {
          const hasValidTokens = !!(state.accessToken && state.refreshToken && state.idToken);
          if (!hasValidTokens) {
            state.isAuthenticated = false;
            state.accessToken = null;
            state.refreshToken = null;
            state.idToken = null;
          } else {
            const idTokenExpired = isTokenExpired(state.idToken, 300);
            const accessTokenExpired = isTokenExpired(state.accessToken, 300);
            
            if (idTokenExpired || accessTokenExpired) {
              state.isAuthenticated = true;
              setTimeout(async () => {
                try {
                  const store = useAdminAuthStore.getState();
                  await store.refreshAccessToken();
                } catch (error) {
                  console.error('Failed to refresh admin token on app startup:', error);
                  const store = useAdminAuthStore.getState();
                  store.signOut();
                }
              }, 0);
            } else {
              state.isAuthenticated = true;
            }
          }
        }
      },
    }
  )
);
